import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Layout, type SectionKey, type ViewKey } from './ui/Layout';
import { TournamentsView } from './ui/TournamentsView';
import { CompaniesView } from './ui/CompaniesView';
import { CompanyProfileView } from './ui/CompanyProfileView';
import { ConfigScreen } from './ui/ConfigScreen';
import { PlayersView } from './ui/PlayersView';
import { GroupStageView } from './ui/GroupStageView';
import { RanksView } from './ui/RanksView';
import { BracketView } from './ui/BracketView';
import { RegistrationsView } from './ui/RegistrationsView';
import { PhotosView } from './ui/PhotosView';
import { UsersView } from './ui/UsersView';
import { MessagingView } from './ui/MessagingView';
import { MobileAdminView } from './ui/MobileAdminView';
import { AthleteHomeView } from './ui/athlete/AthleteHomeView';
import { LoginScreen } from './ui/LoginScreen';
import { PasskeyPromptDialog } from './ui/PasskeyPromptDialog';
import { useTournament, TournamentProvider } from './state/TournamentContext';
import { useAuth } from './state/AuthContext';
import { useTranslation } from './state/I18nContext';
import { toLocale } from './i18n/messages';
import { isSuperAdmin } from './domain';
import type { AthleteLinkView, CurrentUser } from './domain';

// Top-level gate: an unauthenticated visitor only ever sees the login screen.
// The tournament data layer is mounted *after* login so it never issues an
// unauthenticated request on first load.
export function App() {
  const { isAuthenticated, linkVerifying } = useAuth();
  // Redeeming a magic link from the address bar: hold a spinner until it
  // resolves, so we never flash the login screen before the session starts.
  if (linkVerifying) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) {
    return <LoginScreen />;
  }
  return (
    <TournamentProvider>
      <CompanyLocaleSync />
      {/* Above the admin/athlete surface fork, so every fresh password or
          email-link sign-in without a passkey gets the enrolment prompt. */}
      <PasskeyPromptDialog />
      <TournamentApp />
    </TournamentProvider>
  );
}

// Applies the signed-in user's company default language as the UI language
// (unless they explicitly picked one — see `I18nContext`). A company-scoped
// user follows their first accessible company, in the platform's stable
// oldest-first order; Super Admins have no company binding, so they keep the
// device-locale/English resolution. Unmounting (logout) clears the layer so
// the login screen never keeps a previous account's company language.
function CompanyLocaleSync() {
  const { me } = useAuth();
  const { companies } = useTournament();
  const { setCompanyLocale } = useTranslation();

  const companyLocale = useMemo(() => {
    if (!me || me.allCompanies) return null;
    const company = companies.find((c) => me.user.companyIds.includes(c.id));
    return company ? toLocale(company.defaultLanguage) : null;
  }, [me, companies]);

  useEffect(() => {
    setCompanyLocale(companyLocale);
    return () => setCompanyLocale(null);
  }, [companyLocale, setCompanyLocale]);

  return null;
}

// Decides whether this session belongs on the athlete surface rather than the
// club-admin layout (see `features/athlete-access.md`). An athlete or guardian
// account holds athlete links and nothing else — no permissions, no companies —
// so it gets the athlete app outright. A staff member who *also* has an athlete
// profile keeps the admin layout and can switch across; that is the `staffAlso`
// case, which renders the athlete view only on request.
function athleteSurfaceFor(me: CurrentUser | null): {
  links: AthleteLinkView[];
  staffOnly: boolean;
} {
  const links = me?.athleteLinks ?? [];
  const staffOnly =
    me !== null &&
    (me.allCompanies || me.permissions.length > 0 || me.user.companyIds.length > 0);
  return { links, staffOnly };
}

function TournamentApp() {
  const { tournament, selectedId, status, error, companies } = useTournament();
  const { me, hasPermission } = useAuth();
  const theme = useTheme();
  const phoneSized = useMediaQuery(theme.breakpoints.down('sm'));
  // Session-only escape hatch out of the mobile admin surface.
  const [fullSite, setFullSite] = useState(false);
  // Staff who also hold an athlete profile can open the athlete surface for the
  // session; a pure athlete account never leaves it (see `athleteSurfaceFor`).
  const [athleteView, setAthleteView] = useState(false);
  const athleteSurface = athleteSurfaceFor(me);
  const detail = selectedId !== null;
  const started =
    tournament?.status === 'IN_PROGRESS' || tournament?.status === 'FINISHED';

  const [view, setView] = useState<ViewKey>('players');
  const [section, setSection] = useState<SectionKey>('tournaments');
  const [creating, setCreating] = useState(false);
  // The company whose profile page is open (within the Companies section).
  const [companyId, setCompanyId] = useState<number | null>(null);

  // The group stage and bracket only exist once started; a draft falls back to
  // the roster (players) view.
  let activeView: ViewKey = view;
  if (
    !started &&
    (activeView === 'groups' ||
      activeView === 'ranks' ||
      activeView === 'bracket')
  ) {
    activeView = 'players';
  }
  // The registrations tab only exists when public sign-ups are enabled.
  if (activeView === 'registrations' && !tournament?.acceptPublicRegistration) {
    activeView = 'players';
  }

  // The Users section opens to anyone holding a user-management permission
  // (Company Admins manage their own companies' users there); Messaging stays
  // Super-Admin-only. Anyone else — including a session whose access picture
  // is still loading — lands on Tournaments.
  const superAdmin = me !== null && isSuperAdmin(me.user);
  const userManager =
    hasPermission('CREATE_USER') ||
    hasPermission('EDIT_USER') ||
    hasPermission('DELETE_USER');
  let activeSection: SectionKey = section;
  if (
    (activeSection === 'users' && !userManager) ||
    (activeSection === 'messaging' && !superAdmin)
  ) {
    activeSection = 'tournaments';
  }

  // Switching sections abandons an in-progress tournament creation form and
  // any open company profile.
  const changeSection = (next: SectionKey) => {
    setCreating(false);
    setCompanyId(null);
    setSection(next);
  };

  // A company-scoped admin on a phone-sized screen lands on the dedicated
  // mobile surface (attendance + rosters); "full site" opts back out for the
  // session. Super Admins have no company binding and keep the desktop layout.
  const adminCompanies = useMemo(
    () =>
      me !== null && !me.allCompanies
        ? companies.filter((c) => me.user.companyIds.includes(c.id))
        : [],
    [me, companies],
  );
  const mobileAdmin =
    phoneSized &&
    !fullSite &&
    adminCompanies.length > 0 &&
    (hasPermission('MANAGE_TRAINING_CLASSES') || hasPermission('MANAGE_ATHLETES'));

  // First load: wait for the initial list fetch from the API server.
  if (status === 'loading' && !detail) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // An athlete / guardian account has nothing to do in the admin layout: no
  // permissions, no companies, just their own profile(s). Staff who also have a
  // profile land here only when they ask for it.
  if (
    athleteSurface.links.length > 0 &&
    (!athleteSurface.staffOnly || athleteView)
  ) {
    return (
      <AthleteHomeView
        links={athleteSurface.links}
        onFullSite={
          athleteSurface.staffOnly ? () => setAthleteView(false) : undefined
        }
      />
    );
  }

  if (mobileAdmin) {
    return (
      <MobileAdminView
        companies={adminCompanies}
        onFullSite={() => setFullSite(true)}
      />
    );
  }

  return (
    <Layout
      detail={detail}
      view={activeView}
      onChange={setView}
      section={activeSection}
      onSectionChange={changeSection}
      onMyProfile={
        athleteSurface.links.length > 0 ? () => setAthleteView(true) : undefined
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {detail ? (
        <>
          {activeView === 'players' && (
            <PlayersView onStarted={() => setView('groups')} />
          )}
          {activeView === 'groups' && (
            <GroupStageView onViewBracket={() => setView('bracket')} />
          )}
          {activeView === 'ranks' && <RanksView />}
          {activeView === 'bracket' && <BracketView />}
          {activeView === 'registrations' && <RegistrationsView />}
          {activeView === 'photos' && <PhotosView />}
        </>
      ) : activeSection === 'users' ? (
        <UsersView />
      ) : activeSection === 'messaging' ? (
        <MessagingView />
      ) : activeSection === 'companies' ? (
        companyId !== null ? (
          <CompanyProfileView
            companyId={companyId}
            onBack={() => setCompanyId(null)}
          />
        ) : (
          <CompaniesView onOpen={setCompanyId} />
        )
      ) : creating ? (
        <ConfigScreen
          onCreated={() => {
            setCreating(false);
            setView('players');
          }}
          onCancel={() => setCreating(false)}
        />
      ) : (
        <TournamentsView onNew={() => setCreating(true)} />
      )}
    </Layout>
  );
}
